// React and related
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";

// Third-party libraries
import styled, { createGlobalStyle } from "styled-components";
import moment from "moment";
import "moment/locale/hi";
import "moment/locale/en-gb";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import {
  IoChatbubbleOutline,
  IoShareSocialOutline,
  IoHeart,
  IoHeartOutline,
} from "react-icons/io5";
import {
  IoIosArrowBack,
  IoIosArrowForward,
  IoIosArrowDown,
  IoIosArrowUp,
} from "react-icons/io";
import { Link2, Plus } from "lucide-react";

// Local imports - Components
import Context from "../Context";
import Layout from "../Layout/Layout";
import { Helmet } from "react-helmet-async";
import { CustomCarousel } from "../Componets/CustomCarousel";
import { SpotlightCarousel1 } from "../Componets/SpotLight1";
import { useModel } from "../hooks/modelcontex";
import DOBModal from "../Componets/DOBModal";

// Local imports - API
import {
  getHomePagePostsAdditional,
  userLike,
  getRecommendedUsers,
  userFollow,
  getProfileDetails,
} from "../views/utilities/ApiCalls";
import Constant from "./Constant";

// Local imports - Assets
import ProfileImage from "../Componets/ProfileImage";

// Styles
import "../../src/css/style.css";
import "../css/Home.css";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import { setPageTitle } from "../utils/titleUtils";
import { getTokenDetails, renderPreviewHtml } from "./utilities/CommonFunction";
import SindhiFontWoff2 from "../fonts/SHREE-DEV7-0714.woff2";
import SindhiFontWoff from "../fonts/SHREE-DEV7-0714.woff";

const SindhiFontStyles = createGlobalStyle`
  @font-face {
    font-family: 'SHREE-DEV7-0714';
    src:
      url(${SindhiFontWoff2}) format('woff2'),
      url(${SindhiFontWoff}) format('woff');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
`;

// Utility: Fisher-Yates shuffle
function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

export default function Home() {
  // Dev flag for debugging scroll restoration
  const DEV_DEBUG_SCROLL = false; // Set to false to disable debug logs

  const HOME_RESTORE_REASON_KEY = "homeRestoreReason";
  const HOME_RESTORE_REASON_DETAILS = "details";

  const history = useHistory();
  const [posts, setPosts] = useState(() => {
    const saved = sessionStorage.getItem("homePagePosts");
    return saved ? JSON.parse(saved) : [];
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem("homePageCurrentPage");
    return saved ? parseInt(saved, 10) : 1;
  });
  const [hasMore, setHasMore] = useState(() => {
    const saved = sessionStorage.getItem("homePageHasMore");
    return saved === null ? true : saved === "true";
  });
  const [sharelink, setSharelink] = useState(false);
  const [link, setLink] = useState("");
  const [like, setLike] = useState("");
  const [recommendedUsers, setRecommendedUsers] = useState([]);
  const [visibleRecommendedUsersCount, setVisibleRecommendedUsersCount] =
    useState(5);
  const [loadingRecommendedUsers, setLoadingRecommendedUsers] = useState(false);
  const [followStatus, setFollowStatus] = useState({}); // Track follow status for each user

  const [isRestored, setIsRestored] = useState(() => {
    return !!sessionStorage.getItem("homePagePosts");
  });
  const [showDOBModal, setShowDOBModal] = useState(false);
  const scrollRestoredRef = useRef(false); // Track if scroll has been restored to prevent multiple restorations

  const observer = useRef();
  const shareRef = useRef(null);
  const carouselRef = useRef(null); // Ref for CustomCarousel in left sidebar
  const { state, dispatch } = useContext(Context);
  const { t, i18n } = useTranslation();
  const { myprofile, Myprofile } = useModel();
  // console.log("Moment locale:", moment.locale()); // Should match i18n.language
  const location = useLocation();

  // Debug: Check user data availability
  useEffect(() => {
    console.log("=== USER DATA DEBUG ===");
    console.log("myprofile:", myprofile);
    console.log("myprofile?.dob:", myprofile?.dob);
    console.log("state.user:", state?.user);
    console.log("state:", state);
    console.log("=======================");
  }, [myprofile, state]);

  // Check if DOB is missing and show modal
  // TODO: Uncomment when DOB modal is ready for production
  // useEffect(() => {
  //   if (myprofile?._id) {
  //     const dob = myprofile?.dob;
  //     // Check if DOB is empty, null, or empty string
  //     if (!dob || dob.trim() === "") {
  //       setShowDOBModal(true);
  //     }
  //   }
  // }, [myprofile]);

  // Temporarily disable DOB modal for deployment
  useEffect(() => {
    setShowDOBModal(false);
  }, [myprofile]);

  // Handler to refresh profile after DOB update
  const handleDOBUpdateSuccess = async () => {
    try {
      const response = await getProfileDetails();
      if (response.status === "Success" || response.status === "success") {
        Myprofile(response.data.data);
        // Update global isAdult state
        if (response.data.data.isAdult !== undefined) {
          dispatch({ type: "setIsAdult", payload: response.data.data.isAdult });
        }
      }
    } catch (error) {
      console.error("Error refreshing profile:", error);
    }
  };

  useEffect(() => {
    setPageTitle(location.pathname);
  }, [location]);

  // Use a ref to store the new post so we can inject it AFTER the backend fetch completes
  const pendingNewPostRef = useRef(location.state?.newPost || null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("selectedLanguage") || "English";
    const localeMap = {
      English: "en-gb",
      Hindi: "hi",
    };
    const momentLocale = localeMap[savedLanguage];

    i18n.changeLanguage(savedLanguage);
    moment.updateLocale("hi", {
      ordinal: (n) => n,
      numerals: Array.from({ length: 10 }, (_, i) => i.toString()),
    });
    moment.locale(momentLocale);
  }, []);
  // console.log("Moment locale:", moment.locale()); // Should match i18n.language

  useEffect(() => {
    const checklogged = async () => {
      const token = await localStorage.getItem("loginDetails");
      if (!token) {
        localStorage.setItem("redirectAfterLogin", window.location.pathname);
        window.location.href = "/login";
      }
    };
    checklogged();
  }, []);

  // Handle state cleanup on mount if it's a fresh navigation
  useEffect(() => {
    const restoreReason = sessionStorage.getItem(HOME_RESTORE_REASON_KEY);
    const shouldRestoreFromDetails =
      restoreReason === HOME_RESTORE_REASON_DETAILS;

    if (DEV_DEBUG_SCROLL) {
      console.log("🔍 [SCROLL DEBUG] History action:", history.action);
      console.log("🔍 [SCROLL DEBUG] Current scroll position:", window.scrollY);
      console.log(
        "🔍 [SCROLL DEBUG] Saved scroll position:",
        sessionStorage.getItem("homeScrollPosition"),
      );
      console.log(
        "🔍 [SCROLL DEBUG] Restore reason:",
        restoreReason || "(none)",
      );
    }

    console.log("DEBUG: Cleanup effect triggered by history change. shouldRestoreFromDetails:", shouldRestoreFromDetails, "history.action:", history.action, "posts.length:", posts.length);

    const isBackNavigation = history.action === "POP";

    // Only restore cached feed/scroll when we explicitly came from details AND it's a back navigation
    if (!shouldRestoreFromDetails || !isBackNavigation) {
      if (DEV_DEBUG_SCROLL) {
        console.log(
          "🔍 [SCROLL DEBUG] Not restoring from details (or not POP), clearing cache",
        );
      }
      sessionStorage.removeItem("homeScrollPosition");
      sessionStorage.removeItem("homePagePosts");
      sessionStorage.removeItem("homePageCurrentPage");
      sessionStorage.removeItem("homePageHasMore");
      sessionStorage.removeItem(HOME_RESTORE_REASON_KEY);

      // If we initialized from cache but it's a fresh visit, reset it
      if (posts.length > 0) {
        console.log("DEBUG: Wiping posts because it's a fresh visit and posts.length > 0!");
        setPosts([]);
        setPage(1);
        setHasMore(true);
        setIsRestored(false);
      }
      scrollRestoredRef.current = false; // Reset restoration flag
    } else {
      if (DEV_DEBUG_SCROLL) {
        console.log("🔍 [SCROLL DEBUG] Back navigation detected (POP action)");
      }
      // Reset restoration flag on back navigation so it can restore once
      scrollRestoredRef.current = false;
    }
  }, [history]);

  // Track scroll position continuously so we always have the latest value
  useEffect(() => {
    let lastLoggedScroll = 0;
    let scrollLogThrottle = 0;

    const handleScroll = () => {
      if (posts.length > 0) {
        const currentScroll = window.scrollY;
        sessionStorage.setItem("homeScrollPosition", currentScroll.toString());

        // Throttle debug logs - only log when scroll changes significantly or every 2 seconds
        if (DEV_DEBUG_SCROLL) {
          const now = Date.now();
          const scrollDiff = Math.abs(currentScroll - lastLoggedScroll);

          if (scrollDiff > 100 || now - scrollLogThrottle > 2000) {
            console.log(
              "📜 [SCROLL DEBUG] Continuous scroll tracker:",
              currentScroll,
            );
            lastLoggedScroll = currentScroll;
            scrollLogThrottle = now;
          }
        }
      }
    };

    // Also save scroll position on page visibility change (before navigation)
    const handleVisibilityChange = () => {
      if (document.hidden && posts.length > 0) {
        const scrollY = window.scrollY;
        sessionStorage.setItem("homeScrollPosition", scrollY.toString());
        if (DEV_DEBUG_SCROLL) {
          console.log("👁️ [SCROLL DEBUG] Page hidden, saved scroll:", scrollY);
        }
      }
    };

    // Save scroll position on beforeunload (when navigating away)
    const handleBeforeUnload = () => {
      if (posts.length > 0) {
        const scrollY = window.scrollY;
        sessionStorage.setItem("homeScrollPosition", scrollY.toString());
        if (DEV_DEBUG_SCROLL) {
          console.log(
            "🚪 [SCROLL DEBUG] Before unload, saved scroll:",
            scrollY,
          );
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [posts.length]);

  // Save scroll position before navigating away
  useEffect(() => {
    const saveScrollPosition = () => {
      // Check if scroll position was already saved by saveHomePageState()
      // If it was, don't overwrite it with window.scrollY (which might be 0 due to navigation)
      const alreadySaved = sessionStorage.getItem("homeScrollPosition");

      // Only use window.scrollY if we don't have a saved value, or if the saved value is 0
      // This prevents the cleanup from overwriting a valid scroll position saved by saveHomePageState()
      const scrollY =
        alreadySaved && parseInt(alreadySaved, 10) > 0
          ? parseInt(alreadySaved, 10)
          : window.scrollY ||
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          document.body.scrollTop ||
          0;

      sessionStorage.setItem("homeScrollPosition", scrollY.toString());
      sessionStorage.setItem("homePagePosts", JSON.stringify(posts));
      sessionStorage.setItem("homePageCurrentPage", page.toString());
      sessionStorage.setItem("homePageHasMore", hasMore.toString());

      if (DEV_DEBUG_SCROLL) {
        console.log(
          "💾 [SCROLL DEBUG] Saved scroll position in useEffect cleanup:",
          scrollY,
        );
        console.log("💾 [SCROLL DEBUG] Already saved value:", alreadySaved);
        console.log("💾 [SCROLL DEBUG] window.scrollY:", window.scrollY);
        console.log("💾 [SCROLL DEBUG] Using scroll position:", scrollY);
        console.log("💾 [SCROLL DEBUG] Saved posts count:", posts.length);
        console.log("💾 [SCROLL DEBUG] Saved page:", page);
      }
    };

    return () => {
      // Only save if we have posts
      if (posts.length > 0) {
        saveScrollPosition();
      } else {
        if (DEV_DEBUG_SCROLL) {
          console.log("💾 [SCROLL DEBUG] Skipping save - no posts available");
        }
      }
    };
  }, [posts, page, hasMore]);

  // Restore scroll position immediately on mount (before React paints) - useLayoutEffect
  useLayoutEffect(() => {
    // Only restore on back navigation and if not already restored
    const restoreReason = sessionStorage.getItem(HOME_RESTORE_REASON_KEY);
    const shouldRestoreFromDetails =
      restoreReason === HOME_RESTORE_REASON_DETAILS;

    if (
      shouldRestoreFromDetails &&
      history.action === "POP" &&
      !scrollRestoredRef.current
    ) {
      const savedScrollPosition = sessionStorage.getItem("homeScrollPosition");
      const cachedPosts = sessionStorage.getItem("homePagePosts");

      if (savedScrollPosition && cachedPosts && posts.length > 0) {
        const cachedPostsArray = JSON.parse(cachedPosts);
        const scrollPosition = parseInt(savedScrollPosition, 10);

        // Only restore if we have enough posts loaded
        if (scrollPosition > 0 && posts.length >= cachedPostsArray.length) {
          // Immediate restore before React paints
          window.scrollTo(0, scrollPosition);
          document.documentElement.scrollTop = scrollPosition;
          document.body.scrollTop = scrollPosition;

          scrollRestoredRef.current = true; // Mark as restored

          if (DEV_DEBUG_SCROLL) {
            console.log(
              "⚡ [SCROLL DEBUG] useLayoutEffect immediate restore to:",
              scrollPosition,
            );
            console.log(
              "⚡ [SCROLL DEBUG] Posts loaded:",
              posts.length,
              "Cached:",
              cachedPostsArray.length,
            );
          }
        }
      }
    }
  }, [history.action, posts.length]);

  // Force scroll to top specifically for post injection when navigating from Create
  useLayoutEffect(() => {
    if (history.location.state?.newPost && history.action === "PUSH") {
      if (DEV_DEBUG_SCROLL) {
        console.log("⚡ [SCROLL DEBUG] New post injection detected on PUSH. Locking scroll to top.");
      }
      // Use multiple browser APIs to guarantee scroll reset
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // Delay it slightly to beat out any browser native scroll-anchoring after React renders
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 50);
    }
  }, [history.location.state, history.action, posts.length]);

  // Restore scroll position when component mounts (after React paints)
  // Only runs once after back navigation
  useEffect(() => {
    // Only restore on back navigation, if not already restored, and if we have saved data
    const restoreReason = sessionStorage.getItem(HOME_RESTORE_REASON_KEY);
    const shouldRestoreFromDetails =
      restoreReason === HOME_RESTORE_REASON_DETAILS;

    if (!shouldRestoreFromDetails) {
      return; // Skip unless we explicitly came from details
    }

    if (history.action !== "POP" || scrollRestoredRef.current) {
      return; // Skip if not back navigation or already restored
    }

    const savedScrollPosition = sessionStorage.getItem("homeScrollPosition");
    const cachedPosts = sessionStorage.getItem("homePagePosts");

    if (DEV_DEBUG_SCROLL) {
      console.log("🔄 [SCROLL DEBUG] Restore attempt triggered");
      console.log("🔄 [SCROLL DEBUG] Current pathname:", location.pathname);
      console.log(
        "🔄 [SCROLL DEBUG] Saved scroll position:",
        savedScrollPosition,
      );
      console.log("🔄 [SCROLL DEBUG] Cached posts exists:", !!cachedPosts);
      console.log("🔄 [SCROLL DEBUG] Current posts length:", posts.length);
      console.log("🔄 [SCROLL DEBUG] History action:", history.action);
    }

    if (savedScrollPosition && posts.length > 0 && cachedPosts) {
      const cachedPostsArray = JSON.parse(cachedPosts);
      const scrollPosition = parseInt(savedScrollPosition, 10);

      // Only restore if we have the expected number of posts loaded
      if (scrollPosition > 0 && posts.length >= cachedPostsArray.length) {
        if (DEV_DEBUG_SCROLL) {
          console.log(
            "✅ [SCROLL DEBUG] Conditions met, restoring scroll to:",
            scrollPosition,
          );
        }

        // Function to restore scroll using multiple methods
        const restoreScroll = () => {
          // Try multiple methods to ensure scroll works
          window.scrollTo(0, scrollPosition);
          document.documentElement.scrollTop = scrollPosition;
          document.body.scrollTop = scrollPosition;

          if (DEV_DEBUG_SCROLL) {
            const actualScroll =
              window.scrollY ||
              document.documentElement.scrollTop ||
              document.body.scrollTop ||
              0;
            console.log(
              "✅ [SCROLL DEBUG] Executed scrollTo:",
              scrollPosition,
              "Actual scroll:",
              actualScroll,
            );
          }
        };

        // Mark as restored immediately to prevent multiple restorations
        scrollRestoredRef.current = true;

        // Use requestAnimationFrame for better timing
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              restoreScroll();

              // Try once more after a short delay in case React Router resets it
              setTimeout(() => {
                restoreScroll();
              }, 100);

              // Clear all saved home page data immediately after restoring
              // This prevents re-triggering when user scrolls
              sessionStorage.removeItem("homeScrollPosition");
              sessionStorage.removeItem("homePagePosts");
              sessionStorage.removeItem("homePageCurrentPage");
              sessionStorage.removeItem("homePageHasMore");
              sessionStorage.removeItem("homePreviousPath");
              sessionStorage.removeItem(HOME_RESTORE_REASON_KEY);

              if (DEV_DEBUG_SCROLL) {
                console.log(
                  "✅ [SCROLL DEBUG] Cleared sessionStorage after restore",
                );
              }
            }, 50);
          });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length, history.action, location.pathname]); // Trigger when posts are loaded or on navigation

  // Set up intersection observer for infinite scroll
  const lastPostElementRef = useCallback(
    (node) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore],
  );

  // Handle initial data fetch and pagination
  useEffect(() => {
    // If we just restored from cache, skip the fetch which is triggered by setPage/setPosts
    if (isRestored) {
      setIsRestored(false);
      return;
    }

    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);

        // Always use a random page between 1 and 20 for API calls
        const randomPage = Math.floor(Math.random() * 20) + 1;

        let newPosts;
        if (page === 1) {
          //const response = await getHomePagePosts(page);
          //newPosts = response.data || [];
          newPosts = [];
          if (newPosts.length === 0) {
            const additionalResponse =
              await getHomePagePostsAdditional(randomPage);
            newPosts = [
              ...(additionalResponse.data.poems || []),
              ...(additionalResponse.data.stories || []),
            ];
          }
        } else {
          const response = await getHomePagePostsAdditional(randomPage);
          newPosts = [
            ...(response.data.poems || []),
            ...(response.data.stories || []),
          ];
          // newPosts = [];
        }

        //Shuffle the new posts for this page
        newPosts = shuffleArray(newPosts);

        if (newPosts.length === 0) {
          setHasMore(false);
        } else {
          setPosts((prevPosts) => {
            const uniquePosts = newPosts.filter(
              (newPost) => !prevPosts.some((post) => post._id === newPost._id),
            );
            let finalPosts = [...prevPosts, ...uniquePosts];

            // Ensure the backend feed data is gathered first, then prepend the newly created post
            if (page === 1 && pendingNewPostRef.current) {
              console.log("FRONTEND INJECTION: Prepending newly published post to the top of the main feed:", pendingNewPostRef.current);
              const np = pendingNewPostRef.current;

              // Populate user data if missing so the profile picture and name display correctly
              if (!np.user && myprofile) {
                np.user = {
                  _id: myprofile._id,
                  name: myprofile.name,
                  profilePic: myprofile.profilePic
                };
              } else if (!np.authorName && myprofile) {
                np.authorName = myprofile.name;
                np.profilePic = myprofile.profilePic;
              }

              finalPosts = [np, ...finalPosts.filter(p => p._id !== np._id)];

              pendingNewPostRef.current = null; // Mark as injected

              // Safely clear location state to avoid bugs on refresh
              setTimeout(() => {
                if (history.location.state?.newPost) {
                  const stateCopy = { ...history.location.state };
                  delete stateCopy.newPost;
                  history.replace({ ...history.location, state: stateCopy });
                }
              }, 100);
            }

            console.log("DEBUG: Setting finalPosts with length:", finalPosts.length);
            return finalPosts;
          });
        }
      } catch (err) {
        setError("Failed to fetch posts");
        console.error("Error fetching posts:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isRestored]);
  useEffect(() => {
    // console.log("Current language:", i18n.language);
  }, [i18n.language]);

  // Fetch recommended users
  useEffect(() => {
    const fetchRecommendedUsers = async () => {
      if (!myprofile?._id) return; // Wait for profile to be loaded

      try {
        setLoadingRecommendedUsers(true);
        const token = localStorage.getItem("loginDetails");
        if (!token) return;

        const tokenDetails = getTokenDetails(token);
        const userId = tokenDetails?._id || myprofile._id;

        const response = await getRecommendedUsers({
          userId: userId,
          // pageNo not sent - backend will assign random page for shuffling
          limit: 10,
        });

        if (
          response &&
          (response.status === "Success" ||
            response.status === "success" ||
            response.status === Constant.statusSuccess) &&
          response.data &&
          Array.isArray(response.data)
        ) {
          setRecommendedUsers(response.data);
          setVisibleRecommendedUsersCount(5);

          // Initialize follow status for each recommended user
          const initialFollowStatus = {};
          response.data.forEach((writer) => {
            // Check if current user is already following this writer
            const isFollowing =
              writer.followersList?.some(
                (follower) => follower.followerId === myprofile._id,
              ) || false;
            initialFollowStatus[writer._id] = isFollowing;
          });
          setFollowStatus(initialFollowStatus);
        } else {
          console.warn("Invalid response structure or no data:", response);
          setRecommendedUsers([]);
        }
      } catch (error) {
        console.error("Error fetching recommended users:", error);
      } finally {
        setLoadingRecommendedUsers(false);
      }
    };

    fetchRecommendedUsers();
  }, [myprofile?._id]);

  // Handle like functionality
  const getLikesDisplayCount = (post) => {
    if (typeof post.likesCount === "number") return post.likesCount;
    if (typeof post.likes === "number") return post.likes;
    if (Array.isArray(post.likedUserIds)) return post.likedUserIds.length;
    if (Array.isArray(post.likes)) return post.likes.length;
    return 0;
  };

  const handleLike = async (data) => {
    const likeType = data.type === "story" ? 1 : 2;
    const token = localStorage.getItem("loginDetails");
    const tokenDetails = token ? getTokenDetails(token) : null;
    const currentUserId =
      state?.user?._id ||
      state?.user?.id ||
      state?.userId ||
      tokenDetails?._id ||
      tokenDetails?.id ||
      null;

    const likeRequest = {
      moduleId: data._id,
      type: likeType,
      user_id: currentUserId || "",
      userId: currentUserId || "",
      title: data.name,
      description: data.description,
      thumbnail: data.thumbnail,
    };

    try {
      const response = await userLike(likeRequest);
      const isLike = response.message === "Like";

      setPosts((currentPosts) =>
        currentPosts.map((post) => {
          if (post._id === data._id) {
            const existingLikedUserIds = Array.isArray(post.likedUserIds)
              ? post.likedUserIds
              : Array.isArray(post.likes)
                ? post.likes
                : [];
            const alreadyLiked = currentUserId
              ? existingLikedUserIds.includes(currentUserId)
              : post.userLike === 1;

            let updatedLikedUserIds = existingLikedUserIds;
            if (currentUserId) {
              if (isLike && !alreadyLiked) {
                updatedLikedUserIds = [...existingLikedUserIds, currentUserId];
              } else if (!isLike && alreadyLiked) {
                updatedLikedUserIds = existingLikedUserIds.filter(
                  (id) => id !== currentUserId,
                );
              }
            }

            const baseLikesCount = getLikesDisplayCount(post);
            let updatedLikesCount = baseLikesCount;
            if (isLike && !alreadyLiked) {
              updatedLikesCount = baseLikesCount + 1;
            } else if (!isLike && alreadyLiked) {
              updatedLikesCount = Math.max(baseLikesCount - 1, 0);
            }

            return {
              ...post,
              likes:
                typeof post.likes === "number" || !Array.isArray(post.likes)
                  ? updatedLikesCount
                  : updatedLikedUserIds,
              likesCount: updatedLikesCount,
              likedUserIds: updatedLikedUserIds,
              userLike: isLike ? 1 : 0,
            };
          }
          return post;
        }),
      );

      setLike(isLike);

      ReactGA.event({
        category: "User Interaction",
        action: isLike ? "Like" : "Unlike",
        label: likeType === 1 ? "Story Like" : "Poem Like",
        value: data._id,
      });
    } catch (error) {
      console.error("Error handling like:", error);
    }
  };

  // Handle share link functionality
  const handleClickOutside = (event) => {
    if (shareRef.current && !shareRef.current.contains(event.target)) {
      setSharelink(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () =>
      document.removeEventListener("click", handleClickOutside, true);
  }, []);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSharelink(false);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const handleClick = (e) => {
    e.preventDefault(); // stop the SPA navigation
    window.location.href = "/login"; // force full reload
  };

  const proxiedUrl = (url) => {
    if (!url || url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com"))
      return url;
    return `${Constant.apiBasePath}proxy-image?url=${encodeURIComponent(url)}`;
  };

  // Helper function to save home page state before navigation
  const saveHomePageState = (event) => {
    // Get the most recent scroll position from sessionStorage (updated by scroll listener)
    // or fall back to current scroll position
    const savedScroll = sessionStorage.getItem("homeScrollPosition");
    const currentScrollY =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    const scrollY = savedScroll
      ? Math.max(parseInt(savedScroll, 10), currentScrollY)
      : currentScrollY;

    // Save the current pathname so we know where we're navigating from
    sessionStorage.setItem("homePreviousPath", location.pathname);
    sessionStorage.setItem("homeScrollPosition", scrollY.toString());
    sessionStorage.setItem("homePagePosts", JSON.stringify(posts));
    sessionStorage.setItem("homePageCurrentPage", page.toString());
    sessionStorage.setItem("homePageHasMore", hasMore.toString());

    if (DEV_DEBUG_SCROLL) {
      console.log("💾 [SCROLL DEBUG] saveHomePageState() called");
      console.log("💾 [SCROLL DEBUG] Current pathname:", location.pathname);
      if (event && event.currentTarget) {
        console.log(
          "💾 [SCROLL DEBUG] Link target:",
          event.currentTarget.getAttribute("href") ||
          event.currentTarget.getAttribute("to"),
        );
      }
      console.log("💾 [SCROLL DEBUG] Current window.scrollY:", window.scrollY);
      console.log(
        "💾 [SCROLL DEBUG] Current window.pageYOffset:",
        window.pageYOffset,
      );
      console.log(
        "💾 [SCROLL DEBUG] Current document.documentElement.scrollTop:",
        document.documentElement.scrollTop,
      );
      console.log(
        "💾 [SCROLL DEBUG] Current document.body.scrollTop:",
        document.body.scrollTop,
      );
      console.log(
        "💾 [SCROLL DEBUG] Saved scroll from sessionStorage:",
        savedScroll,
      );
      console.log(
        "💾 [SCROLL DEBUG] Using scroll position (max of saved and current):",
        scrollY,
      );
      console.log("💾 [SCROLL DEBUG] Saved posts count:", posts.length);
      console.log("💾 [SCROLL DEBUG] Call stack:", new Error().stack);
    }
  };

  // Save state specifically for details navigation (so Home can restore on back)
  const saveHomePageStateForDetails = (event) => {
    sessionStorage.setItem(
      HOME_RESTORE_REASON_KEY,
      HOME_RESTORE_REASON_DETAILS,
    );
    saveHomePageState(event);
  };

  // Capture scroll position on mouseDown (before click/navigation)
  const captureScrollOnInteraction = (e) => {
    // Try multiple methods to get the most accurate scroll position
    const scrollY =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    const savedScroll = sessionStorage.getItem("homeScrollPosition");

    // Use the maximum of current scroll and saved scroll (in case user scrolled between saves)
    const scrollToSave = Math.max(
      scrollY,
      savedScroll ? parseInt(savedScroll, 10) : 0,
    );

    sessionStorage.setItem("homeScrollPosition", scrollToSave.toString());
    if (DEV_DEBUG_SCROLL) {
      console.log("🖱️ [SCROLL DEBUG] Captured scroll on interaction:");
      console.log("   - window.scrollY:", window.scrollY);
      console.log("   - window.pageYOffset:", window.pageYOffset);
      console.log(
        "   - document.documentElement.scrollTop:",
        document.documentElement.scrollTop,
      );
      console.log("   - document.body.scrollTop:", document.body.scrollTop);
      console.log("   - Saved scroll from storage:", savedScroll);
      console.log("   - Using scroll position:", scrollToSave);
      console.log("   - Event type:", e.type);
    }
  };

  // Handle follow/unfollow functionality
  const handleFollow = async (writerId, writer) => {
    if (!myprofile?._id) {
      console.warn("User profile not available");
      return;
    }

    const currentStatus = followStatus[writerId] || false;
    const newStatus = !currentStatus;
    const userStatus = newStatus ? 1 : 0;

    try {
      const userRequest = {
        followingId: writerId,
        status: userStatus,
      };

      await userFollow(userRequest);

      // Update follow status for this specific user
      setFollowStatus((prev) => ({
        ...prev,
        [writerId]: newStatus,
      }));

      // Optionally update the recommendedUsers array to reflect the change
      setRecommendedUsers((prevUsers) =>
        prevUsers.map((user) => {
          if (user._id === writerId) {
            // Update followersList if it exists
            if (newStatus) {
              // Add current user to followersList
              const updatedFollowersList = user.followersList || [];
              if (
                !updatedFollowersList.some(
                  (f) => f.followerId === myprofile._id,
                )
              ) {
                updatedFollowersList.push({ followerId: myprofile._id });
              }
              return { ...user, followersList: updatedFollowersList };
            } else {
              // Remove current user from followersList
              const updatedFollowersList = (user.followersList || []).filter(
                (f) => f.followerId !== myprofile._id,
              );
              return { ...user, followersList: updatedFollowersList };
            }
          }
          return user;
        }),
      );
    } catch (error) {
      console.error("Error following/unfollowing user:", error);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>StoryTent | Read, Write & Share Stories, Poems and Open Mics</title>
        <meta name="description" content="StoryTent is a global community of storytellers, poets, and performers. Discover, write, and share original stories, poems, audio, and join live open mics." />
        <meta property="og:title" content="StoryTent | Global Platform for Stories & Poems" />
        <meta property="og:description" content="Discover, write, and share original stories, poems, audio, and join live open mics on StoryTent." />
        <meta property="og:url" content="https://www.storytent.in/" />
        <link rel="canonical" href="https://www.storytent.in/" />
      </Helmet>
      <SindhiFontStyles />
      <div
        style={{
          width: "100%",
          justifyContent: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        {/* Mobile: Events and Spotlight Carousels at Top */}
        <MobileCarouselsContainer>
          <div className="mobile-events-carousel">
            <CustomCarousel />
          </div>
          <div className="mobile-spotlight-carousel">
            <SpotlightCarousel1 />
          </div>
        </MobileCarouselsContainer>

        <RootGrid>
          {/* Left Sidebar: Spotlight */}
          <div className="left-sidebar">
            <CarouselWrapper>
              <div style={{ width: "100%", maxWidth: "100%" }}>
                <CustomCarousel ref={carouselRef} />
              </div>
              <CarouselArrowLeft
                onClick={() => {
                  if (carouselRef.current) {
                    carouselRef.current.goToPrev();
                  }
                }}
              >
                <IoIosArrowBack
                  color="black"
                  style={{
                    fontWeight: "900",
                    fontSize: "16px",
                    strokeWidth: "35px",
                  }}
                />
              </CarouselArrowLeft>
              <CarouselArrowRight
                onClick={() => {
                  if (carouselRef.current) {
                    carouselRef.current.goToNext();
                  }
                }}
              >
                <IoIosArrowForward
                  color="black"
                  style={{
                    fontWeight: "900",
                    fontSize: "16px",
                    strokeWidth: "35px",
                  }}
                />
              </CarouselArrowRight>
            </CarouselWrapper>
            <div style={{ width: "100%", maxWidth: "100%" }}>
              <SpotlightCarousel1 />
            </div>
          </div>

          {/* Main Feed Column */}
          <div className="main-feed">
            <div className="feed-header">
              <h2>Trending in the Tent</h2>
            </div>

            <Root>
              <div className={`posts ${sharelink && "opacity"}`}>
                {posts.map((data, index) => {
                  const isSindhiPost =
                    (data.language || "").toLowerCase() === "sindhi";
                  const isLastElement = posts.length === index + 1;
                  const userId = data?.user?._id || data?.userId;
                  const userName = data?.user?.name || data?.authorName;
                  const profilePic = data?.user?.profilePic ?? data?.profilePic;
                  return (
                    <div
                      key={data._id}
                      ref={isLastElement ? lastPostElementRef : null}
                      className="post-container"
                    >
                      {/* Header: Author Info */}
                      <Link
                        to={
                          myprofile._id === userId
                            ? `/profile`
                            : `/userprofile/${userId}`
                        }
                        className="user-profile"
                        onMouseDown={captureScrollOnInteraction}
                        onClick={saveHomePageState}
                      >
                        <ProfileImage
                          src={profilePic}
                          name={userName}
                          size={40}
                          className="profile"
                          alt="profile"
                          proxiedUrl={proxiedUrl}
                        />
                        <div className="name">
                          <span className="author-name">{userName}</span>
                          {/* <span className="duration">
                            {moment(data?.createdAt)
                              .locale(i18n.language)
                              .fromNow()}
                          </span> */}
                        </div>
                      </Link>

                      {/* Body: Title, Description, Thumbnail */}
                      {state.token ? (
                        <Link
                          to={`/${data.type}-details/${data._id}`}
                          className="main-content"
                          onMouseDown={captureScrollOnInteraction}
                          onClick={saveHomePageStateForDetails}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <div className="post-info">
                            <h3
                              className="heading"
                              style={
                                isSindhiPost
                                  ? {
                                    fontFamily: "SHREE-DEV7-0714",
                                    fontSize: "24px",
                                  }
                                  : undefined
                              }
                            >
                              {data.name}
                            </h3>
                            <p
                              className={`text${isSindhiPost ? " sindhi-text" : ""
                                }`}
                            >
                              {renderPreviewHtml(
                                data.description &&
                                  data.description.trim() !== ""
                                  ? data.description
                                  : data.story,
                                true,
                              )}
                            </p>
                            <div className="read-full-desktop">
                              Read full{" "}
                              {data.type === "poem" ? "poem" : "story"} &rarr;
                            </div>
                          </div>
                          <div className="thumbnail-img">
                            <img
                              alt=""
                              className="img"
                              src={data.thumbnail}
                            ></img>
                          </div>
                        </Link>
                      ) : (
                        <div
                          className="main-content"
                          onClick={() => {
                            window.location.href = "/login";
                          }}
                        >
                          <div className="post-info">
                            <h3
                              className="heading"
                              style={
                                isSindhiPost
                                  ? { fontFamily: "SHREE-DEV7-0714" }
                                  : undefined
                              }
                            >
                              {data.name}
                            </h3>
                            <p
                              className={`text${isSindhiPost ? " sindhi-text" : ""
                                }`}
                            >
                              {renderPreviewHtml(
                                data.description &&
                                  data.description.trim() !== ""
                                  ? data.description
                                  : data.story,
                                true,
                              )}
                            </p>
                            <div className="read-full-desktop">
                              Read full{" "}
                              {data.type === "poem" ? "poem" : "story"} &rarr;
                            </div>
                          </div>
                          <div className="thumbnail-img">
                            <img
                              alt=""
                              className="img"
                              src={data.thumbnail}
                            ></img>
                          </div>
                        </div>
                      )}

                      {/* Footer: Icons and Read Button */}
                      <div className="post-footer">
                        <div className="icons">
                          {state.token ? (
                            <>
                              <span
                                className="icon-item"
                                onClick={() => handleLike(data)}
                              >
                                {data.userLike ? (
                                  <IoHeart
                                    style={{
                                      fontSize: "18px",
                                      color: "#FF0000",
                                      cursor: "pointer",
                                    }}
                                  />
                                ) : (
                                  <IoHeartOutline
                                    style={{
                                      fontSize: "18px",
                                      color: "#666",
                                      strokeWidth: "1.5",
                                    }}
                                  />
                                )}
                                <span className="count">
                                  {getLikesDisplayCount(data)}
                                </span>
                              </span>

                              <span className="icon-item">
                                <Link
                                  to={`/${data.type}-details/${data._id}`}
                                  onMouseDown={captureScrollOnInteraction}
                                  onClick={saveHomePageStateForDetails}
                                  style={{
                                    textDecoration: "none",
                                    color: "inherit",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <IoChatbubbleOutline
                                    style={{
                                      fontSize: "18px",
                                      color: "#666",
                                      cursor: "pointer",
                                      strokeWidth: "1.5",
                                    }}
                                  />
                                  <span className="count">
                                    {data.number_of_comments}
                                  </span>
                                </Link>
                              </span>
                              <span className="icon-item">
                                <IoShareSocialOutline
                                  style={{
                                    fontSize: "18px",
                                    color: "#666",
                                    cursor: "pointer",
                                    strokeWidth: "1.5",
                                  }}
                                  onClick={() => {
                                    setSharelink(true);
                                    setLink(
                                      `https://www.storytent.in/${data.type}-details/${data._id}`,
                                    );
                                  }}
                                />
                              </span>
                            </>
                          ) : (
                            <Link
                              to="/login"
                              onClick={handleClick}
                              style={{
                                color: "var(--color-text)",
                                display: "flex",
                                gap: "20px",
                              }}
                            >
                              <IoHeartOutline
                                style={{
                                  fontSize: "18px",
                                  color: "#666",
                                  strokeWidth: "1.5",
                                }}
                              />
                              <IoChatbubbleOutline
                                style={{
                                  fontSize: "18px",
                                  color: "#666",
                                  strokeWidth: "1.5",
                                }}
                              />
                              <IoShareSocialOutline
                                style={{
                                  fontSize: "18px",
                                  color: "#666",
                                  strokeWidth: "1.5",
                                }}
                              />
                            </Link>
                          )}
                        </div>
                        {state.token ? (
                          <Link
                            to={`/${data.type}-details/${data._id}`}
                            className="read-more-mobile"
                            onMouseDown={captureScrollOnInteraction}
                            onClick={saveHomePageStateForDetails}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            Read &rarr;
                          </Link>
                        ) : (
                          <div
                            className="read-more-mobile"
                            onClick={() => {
                              window.location.href = "/login";
                            }}
                          >
                            Read &rarr;
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {loading && (
                  <div
                    style={{
                      fontSize: "20px",
                      textAlign: "center",
                      color: "var(--color-text-tertiary)",
                    }}
                  >
                    {t("Home.Loading")}
                  </div>
                )}

                {error && (
                  <div
                    style={{
                      fontSize: "20px",
                      textAlign: "center",
                      color: "red",
                    }}
                  >
                    {error}
                  </div>
                )}

                {!hasMore && (
                  <div
                    style={{
                      fontSize: "20px",
                      textAlign: "center",
                      color: "var(--color-text-tertiary)",
                    }}
                  >
                    No more posts to load
                  </div>
                )}
              </div>

              {sharelink && (
                <div ref={shareRef} className="share-box">
                  <div
                    onClick={() => setSharelink(false)}
                    style={{ position: "absolute", right: "0px" }}
                  >
                    <Plus
                      style={{
                        transform: "rotate(45deg)",
                        color: "white",
                        cursor: "pointer",
                      }}
                    ></Plus>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      color: "white",
                      fontSize: "20px",
                    }}
                  >
                    {t("ShareLink.Share")}
                  </div>

                  <div>
                    <div className="share-link-icon">
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          copyToClipboard(link);
                          setSharelink(false);
                        }}
                        className="icon-container"
                      >
                        <Link2></Link2>
                      </span>
                    </div>
                    <div
                      style={{
                        textAlign: "center",
                        color: "white",
                      }}
                    >
                      {t("ShareLink.CopyLink")}
                    </div>
                  </div>
                </div>
              )}
            </Root>
          </div>

          {/* Right Sidebar: Empty for now */}
          <div className="right-sidebar">
            {/* Writers to Follow Placeholder */}
            <WritersContainer>
              <div className="section-header">
                <h3>WRITERS TO FOLLOW</h3>
              </div>
              <div className="writers-list">
                {loadingRecommendedUsers ? (
                  <div
                    style={{
                      padding: "10px",
                      color: "var(--color-text-secondary)",
                    }}
                  >
                    Loading...
                  </div>
                ) : recommendedUsers.length > 0 ? (
                  recommendedUsers.slice(0, visibleRecommendedUsersCount).map((writer) => {
                    return (
                      <div key={writer._id} className="writer-item">
                        <Link
                          to={
                            myprofile._id === writer._id
                              ? `/profile`
                              : `/userprofile/${writer._id}`
                          }
                          className="writer-info"
                          onClick={saveHomePageState}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <ProfileImage
                            src={writer?.profilePic}
                            name={writer?.name || writer?.userName}
                            size={32}
                            className="writer-avatar"
                            alt={writer?.name || writer?.userName}
                            proxiedUrl={proxiedUrl}
                          />
                          <span className="writer-name">
                            {writer.name || writer.userName}
                          </span>
                        </Link>
                        <button
                          className="follow-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFollow(writer._id, writer);
                          }}
                        >
                          {followStatus[writer._id] ? "Unfollow" : "Follow"}
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      padding: "10px",
                      color: "var(--color-text-secondary)",
                    }}
                  >
                    No recommendations available
                  </div>
                )}
              </div>
              {!loadingRecommendedUsers && recommendedUsers.length > 5 && (
                <div className="load-more-wrap">
                  <button
                    className="load-more-btn"
                    onClick={() =>
                      setVisibleRecommendedUsersCount((prevCount) =>
                        prevCount >= 10
                          ? 5
                          : Math.min(prevCount + 5, 10, recommendedUsers.length),
                      )
                    }
                  >
                    {visibleRecommendedUsersCount >= 10
                      ? "Show less"
                      : "Show more"}
                    {visibleRecommendedUsersCount >= 10 ? (
                      <IoIosArrowUp className="load-more-chevron" size={14} />
                    ) : (
                      <IoIosArrowDown className="load-more-chevron" size={14} />
                    )}
                  </button>
                </div>
              )}
            </WritersContainer>
          </div>
        </RootGrid>
      </div>

      {/* DOB Modal */}
      <DOBModal
        isOpen={showDOBModal}
        onClose={() => setShowDOBModal(false)}
        onSuccess={handleDOBUpdateSuccess}
      />
    </Layout>
  );
}

const WritersContainer = styled.div`
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 20px;
  width: 100%;
  box-sizing: border-box;

  .section-header {
    margin-bottom: 15px;
    h3 {
      font-size: 12px;
      font-weight: 700;
      color: var(--color-text-secondary);
      text-transform: uppercase;
      letter-spacing: 1px;
      margin: 0;
    }
  }

  .writers-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .writer-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .writer-info {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    flex: 1;
    min-width: 0;

    &:hover .writer-name {
      text-decoration: underline;
    }
  }

  .writer-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
  }

  .writer-name {
    display: block;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-tertiary);
  }

  .follow-btn,
  .load-more-btn {
    background: transparent;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    font-family: "Roboto", sans-serif;
    font-weight: 500;
    color: var(--color-text-tertiary);
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
      border-color: var(--color-text-secondary);
    }

    &:focus-visible {
      box-shadow: 0 0 0 2px rgba(70, 92, 76, 0.2);
    }
  }

  .follow-btn {
    padding: 4px 12px;
    font-size: 12px;
  }

  .load-more-btn {
    width: fit-content;
    min-width: 120px;
    padding: 8px 14px;
    font-size: 13px;
    color: var(--color-text);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    text-align: center;

    .load-more-chevron {
      width: 14px;
      height: 14px;
      transition: transform 0.2s ease;
    }

    &:hover .load-more-chevron {
      transform: translateY(1px);
    }
  }

  .load-more-wrap {
    margin-top: 12px;
    display: flex;
    justify-content: center;
  }

  .view-all {
    margin-top: 20px;
    font-size: 13px;
    color: var(--color-text-secondary);
    cursor: pointer;
    text-align: center;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const RootGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr); // 25-50-25 ratio
  gap: 20px;
  width: 100%;
  max-width: 1400px;
  margin-top: 20px;
  padding: 0 20px;

  @media (max-width: 960px) {
    grid-template-columns: 280px 1fr; // Tablet: Fixed width sidebar, flexible main feed
    gap: 15px;
    padding: 0 15px;
    .right-sidebar {
      display: none;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr; // Mobile: Single column
    gap: 20px;
    padding: 0 20px;
    .left-sidebar,
    .right-sidebar {
      display: none; // Or move to top/bottom if needed
    }
  }

  .left-sidebar {
    /* Sticky sidebar optional */
    position: sticky;
    top: calc(var(--home-fixed-nav-height, 0px) + 20px);
    height: fit-content;
    max-width: 100%;
    overflow: visible; /* Changed to visible to allow carousel arrows to show */
    min-width: 0; // Prevent grid overflow
    display: flex;
    flex-direction: column;
    gap: 20px;

    @media (max-width: 960px) {
      max-width: 280px; // Constrain sidebar width on tablet
      gap: 15px;
    }

    @media (max-width: 768px) {
      display: none !important; // Hide sidebar on mobile to prevent duplicate carousels
    }

    > div {
      width: 100%;
      max-width: 100%;
      min-width: 0; // Prevent stretching
    }
  }

  .main-feed {
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-width: 100%; /* Ensure it doesn't overflow its grid cell */
    min-width: 0; // Prevent grid overflow

    @media (max-width: 960px) {
      min-width: 0; // Allow main feed to shrink on tablet
    }
  }

  .feed-header {
    padding: 8px 0 4px 0; /* Reduced vertical spacing above and below */
    border-bottom: none;
    h2 {
      color: var(--color-text);
      font-size: 24px;
      margin: 0;
      font-family: "Merriweather", serif; // Serif for section heading
      font-weight: 700;
    }
  }

  .right-sidebar {
    position: sticky;
    top: calc(var(--home-fixed-nav-height, 0px) + 20px);
    height: fit-content;
  }
`;

const MobileCarouselsContainer = styled.div`
  display: none;
  width: 100%;
  max-width: 1400px;
  padding: 0 20px;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;

  @media (max-width: 768px) {
    display: flex;
  }

  .mobile-events-carousel,
  .mobile-spotlight-carousel {
    width: 100%;
  }
`;

const Root = styled.div`
  min-height: 582px;
  height: auto;
  display: flex;
  /* background-color: #0f1817;  Removed to allow notebook bg to show */
  width: 100%;
  /* max-width: 1200px; Removed max-width here, controlled by grid */
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;
  .share-box {
    width: 300px;
    height: 120px;
    border-radius: 10px;
    background-color: #023020;
    position: fixed;
    top: 400px;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000; // Added z-index
  }

  .icon-container {
    background-color: #e6edea;
    padding: 10px;
    padding-left: 12px;
    padding-right: 12px;
    border-radius: 100px;
  }

  .share-link-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .opacity {
    opacity: 0.2;
  }

  .post-container {
    background-color: var(--color-surface);
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    border: 1px solid var(--color-border);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05); // Softer shadow
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 15px;

    @media (max-width: 768px) {
      padding: 15px;
      gap: 12px;
    }
  }

  .user-profile {
    display: flex;
    gap: 10px;
    color: var(--color-text-secondary);
    align-items: center;
    padding-left: 0;
    padding-right: 0;
    text-decoration: none;
  }

  .profile {
    height: 40px;
    width: 40px;
    border-radius: 50%;
    object-fit: cover;
  }

  .name {
    display: flex;
    flex-direction: column; /* Changed to column for stacking */
    align-items: flex-start; /* Align to start */
    gap: 0px; /* Further reduced gap between author and date */
    font-weight: 500;
    color: var(--color-text-tertiary); /* Changed to use theme variable */
    text-decoration: none;

    .author-name {
      font-weight: 700;
      font-size: 15px;
      color: var(--color-text-tertiary); /* Changed to use theme variable */
      text-decoration: none;
      font-family: "Roboto", sans-serif;
    }

    .duration {
      font-size: 11px;
      color: #808080; /* Same grey as action buttons */
      /* Removed pseudo-element since we are using explicit bullet */
      text-decoration: none;
      font-family: "Roboto", sans-serif;
    }
  }

  .heading {
    margin: 0 0 8px 0;
    font-size: 22px;
    line-height: 1.5;
    font-weight: 700;
    font-family: "Libre Baskerville", serif;
    color: var(--color-text-tertiary); /* Changed to use theme variable */
    cursor: pointer;

    @media (max-width: 768px) {
      font-size: 18px;
    }
  }

  .main-content {
    display: flex;
    gap: 15px;
    align-items: flex-start;
    justify-content: space-between;
    height: auto;
    cursor: pointer;
    width: 100%;
    padding: 0;
  }

  .post-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: visible;
    height: auto;
    min-width: 0;
  }

  .text {
    width: 100%;
    margin: 0;
    line-height: 1.5;
    color: var(--color-text-tertiary); /* Changed to use theme variable */
    font-size: 15px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4; // Limit to 4 lines
    white-space: normal;
    text-overflow: ellipsis;
    text-align: left;
    font-family: "Libre Baskerville", serif;

    @media (max-width: 768px) {
      font-size: 14px;
      -webkit-line-clamp: 3;
    }
  }

  .text.sindhi-text {
    font-family: "SHREE-DEV7-0714", "Libre Baskerville", serif;
    font-size: 17px;
  }

  .read-full-desktop {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text); /* Changed to use theme variable */
    cursor: pointer;
    margin-top: 10px;
    display: block;
    font-family: "Roboto", sans-serif;

    &:hover {
      text-decoration: underline;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  .thumbnail-img {
    width: 100px;
    height: 100px;
    flex-shrink: 0; // Prevent shrinking
    border-radius: 8px;
    overflow: hidden;

    @media (max-width: 768px) {
      width: 80px;
      height: 80px;
    }
  }

  .img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    margin-top: 0px;
  }

  .post-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 5px;
  }

  .icons {
    display: flex;
    gap: 25px;
    margin: 0;
    padding: 0;
    align-items: center;
    color: #808080; /* Make icons/text gray */

    svg {
      color: #808080;
      stroke: #808080;
    }
  }

  .icon-item {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: #808080; /* Gray color for icon and text */

    .count {
      font-size: 14px;
      font-weight: 500;
    }
  }

  .read-more-mobile {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text); /* Changed to use theme variable */
    cursor: pointer;
    display: none; /* Hidden by default on desktop */
    align-items: center;
    font-family: "Roboto", sans-serif;

    &:hover {
      text-decoration: underline;
    }

    @media (max-width: 768px) {
      display: flex; /* Show on mobile */
    }
  }

  .read-more {
    font-size: 14px;
    font-weight: 600;
    color: #465c4c; // Using primary/granite color directly or from var
    cursor: pointer;
    display: flex;
    align-items: center;

    &:hover {
      text-decoration: underline;
    }
  }

  .input {
    width: 100%;
    padding: 5px;
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
    outline: none;
    border: none;
    display: block;
    padding-left: 10px;
    background-color: #263a38;
  }

  .share-button {
    color: white;
    padding: 5px;
    padding-right: 10px;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    outline: none;
    border: none;
    background-color: #5a6f6d;
  }
  .input-container {
    display: flex;
    width: 100%;

    ${"" /* justify-content:space-between; */}
  }

  .label {
    color: white;
  }

  .form {
    margin-top: 75px;
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .slider {
    background-color: #263a38;
    color: white;
    ${"" /* margin-top:75px; */}

    width:100%;
    height: 150px;
    display: flex;
    border-radius: 15px;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .inside {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .slider-container {
    margin-top: 75px;
    width: 80%;
    height: auto;
    min-height: 150px;
    ${"" /* max-width:600px; */}
    ${"" /* background-color:red; */}
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  margin-bottom: 20px;
  min-width: 0; // Prevent stretching

  @media (max-width: 960px) {
    max-width: 280px; // Constrain carousel width on tablet
  }

  @media (max-width: 768px) {
    /* Hide arrows on mobile */
    .carousel-arrow {
      display: none;
    }
  }
`;

const CarouselArrowLeft = styled.div`
  position: absolute;
  top: 55%;
  left: -5px;
  transform: translate(0, -50%);
  cursor: pointer;
  z-index: 100;
  color: white;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(194, 197, 197, 0.75);
  border-radius: 50%;
  pointer-events: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CarouselArrowRight = styled.div`
  position: absolute;
  top: 55%;
  right: -5px;
  transform: translate(0, -50%);
  cursor: pointer;
  z-index: 100;
  color: white;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(194, 197, 197, 0.75);
  border-radius: 50%;
  pointer-events: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;
